import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/libs/utils'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
  text?: string
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
}

export function LoadingSpinner({ size = 'md', className, text }: LoadingSpinnerProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
      <div
        className={cn(
          'border-2 border-muted border-t-primary rounded-full animate-spin',
          sizeClasses[size],
        )}
      />
      {text && <p className="text-sm text-muted-foreground">{text}</p>}
    </div>
  )
}

function SkeletonBackground() {
  return (
    <div className="absolute inset-0 p-6 space-y-6 pointer-events-none">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
      </div>

      {/* Filter bar skeleton */}
      <div className="flex gap-3">
        <Skeleton className="h-9 w-64 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>

      {/* Stat cards skeleton */}
      <div className="grid grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border/50 p-4 space-y-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>

      {/* Table skeleton */}
      <div className="rounded-lg border border-border/50">
        {/* Table header */}
        <div className="flex gap-4 p-3 border-b border-border/30">
          {[120, 100, 80, 140, 80, 60].map((w, i) => (
            <Skeleton key={i} className="h-4 rounded" style={{ width: w }} />
          ))}
        </div>
        {/* Table rows */}
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="flex gap-4 p-3 border-b border-border/20 last:border-0">
            {[120, 100, 80, 140, 80, 60].map((w, i) => (
              <Skeleton
                key={i}
                className="h-4 rounded"
                style={{ width: w, opacity: 1 - row * 0.1 }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function PitstopOverlay() {
  const { t } = useTranslation()
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10">
      {/* Translucent backdrop */}
      <div className="absolute inset-0 bg-background/30 backdrop-blur-[1px]" />

      {/* Car animation */}
      <div className="relative z-20 flex flex-col items-center gap-5">
        <div className="relative w-48 h-24">
          {/* Road */}
          <div className="absolute bottom-2 left-0 right-0 h-[3px] bg-border rounded-full" />
          <div className="absolute bottom-[11px] left-4 right-4 flex justify-between">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="w-3 h-[2px] bg-muted-foreground/30 rounded-full animate-[road-dash_1s_linear_infinite]"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>

          {/* Car body */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-[car-bounce_1.2s_ease-in-out_infinite]">
            <div className="relative">
              {/* Roof */}
              <div className="w-14 h-5 bg-primary rounded-t-lg mx-auto -mb-[1px] relative">
                <div className="absolute inset-x-1 top-1 bottom-0 flex gap-[2px]">
                  <div className="flex-1 bg-primary-foreground/20 rounded-tl-md" />
                  <div className="w-[2px] bg-primary" />
                  <div className="flex-1 bg-primary-foreground/20 rounded-tr-md" />
                </div>
              </div>
              {/* Body */}
              <div className="w-24 h-5 bg-primary rounded-md relative">
                <div className="absolute right-[2px] top-1 w-2 h-2 bg-warning rounded-full animate-[headlight_1.5s_ease-in-out_infinite]" />
                <div className="absolute left-[2px] top-1 w-1.5 h-2 bg-destructive/70 rounded-full" />
              </div>
              {/* Wheels */}
              <div className="absolute -bottom-2 left-3 w-5 h-5 bg-foreground rounded-full border-2 border-muted-foreground flex items-center justify-center animate-[wheel-spin_0.6s_linear_infinite]">
                <div className="w-1.5 h-1.5 bg-muted rounded-full" />
                <div className="absolute w-full h-[1px] bg-muted-foreground/50" />
                <div className="absolute w-[1px] h-full bg-muted-foreground/50" />
              </div>
              <div className="absolute -bottom-2 right-3 w-5 h-5 bg-foreground rounded-full border-2 border-muted-foreground flex items-center justify-center animate-[wheel-spin_0.6s_linear_infinite]">
                <div className="w-1.5 h-1.5 bg-muted rounded-full" />
                <div className="absolute w-full h-[1px] bg-muted-foreground/50" />
                <div className="absolute w-[1px] h-full bg-muted-foreground/50" />
              </div>
            </div>
          </div>

          {/* Wrench */}
          <div className="absolute top-0 right-6 animate-[tool-float_2s_ease-in-out_infinite]">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-muted-foreground/60"
            >
              <path
                d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Loading text */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground font-medium">
          <span>{t('common.loadingLabel')}</span>
          <span className="flex gap-[2px]">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="inline-block w-1 h-1 bg-muted-foreground rounded-full animate-[dot-pulse_1.4s_ease-in-out_infinite]"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </span>
        </div>
      </div>
    </div>
  )
}

export function PageLoader() {
  return (
    <div className="relative min-h-[400px] overflow-hidden rounded-lg">
      <SkeletonBackground />
      <PitstopOverlay />
    </div>
  )
}

export function FullPageLoader() {
  return (
    <div className="fixed inset-0 bg-background">
      <SkeletonBackground />
      <PitstopOverlay />
    </div>
  )
}
